import { DI } from '@intigral/constant/config/DIContainer';
import { container } from '../../DataDomain';
import {
    IConsentRequest,
    IConsentResponse,
    IPPVCountryConfigResponse,
    IPPVEventConfigResponse,
    IPPVPurchasePayload,
    IPPVPurchaseResponse,
} from '../../entity';
import { IGetConsentUseCase } from '../../core/usecases/userRegistration/GetConsentUseCase';
import { IAddConsentUseCase } from '../../core/usecases/userRegistration/AddConsentUseCase';
import { IGetPPVEventConfigUseCase } from '../../core/usecases/userRegistration/GetPPVEventConfigUseCase';
import { IGetPPVCountryConfigUseCase } from '../../core/usecases/userRegistration/GetPPVCountryConfigUseCase';
import { IPPVPurchaseUseCase } from '../../core/usecases/userRegistration/GetPPVPurchaseUseCase';
import { IFormSignatureObjectUseCase } from '../../core/usecases/auth/FormSignatureObjectUseCase';

export interface IUserRegistrationService {
    addConsent(
        apiKey: string,
        paylod: IConsentRequest,
        token: string,
        identityToken: string
    ): Promise<IConsentResponse['data']>;

    getConsent(apiKey: string, token: string, identityToken: string): Promise<IConsentResponse['data']>;

    getPPVEventConfig(): Promise<IPPVEventConfigResponse['data']>;
    getPPVCountryConfig(country?: string): Promise<IPPVCountryConfigResponse['data']>;
    ppvPurchase(
        payload: IPPVPurchasePayload,
        authToken: string,
        authSign: string,
        identityToken: string
    ): Promise<IPPVPurchaseResponse>;
}

export class UserRegistrationService implements IUserRegistrationService {
    // Lazy getters for dependencies to avoid circular dependencies
    private get formSignatureObjectUseCase(): IFormSignatureObjectUseCase {
        return container.resolve<IFormSignatureObjectUseCase>(DI.USECASES.IFormSignatureObjectUseCase);
    }

    private get addConsentUseCase(): IAddConsentUseCase {
        return container.resolve<IAddConsentUseCase>(DI.USECASES.IAddConsentUseCase);
    }

    private get getConsentUseCase(): IGetConsentUseCase {
        return container.resolve<IGetConsentUseCase>(DI.USECASES.IGetConsentUseCase);
    }

    private get getPPVEventConfigUseCase(): IGetPPVEventConfigUseCase {
        return container.resolve<IGetPPVEventConfigUseCase>(DI.USECASES.IGetPPVEventConfigUseCase);
    }

    private get getPPVCountryConfigUseCase(): IGetPPVCountryConfigUseCase {
        return container.resolve<IGetPPVCountryConfigUseCase>(DI.USECASES.IGetPPVCountryConfigUseCase);
    }

    private get getPPVPurchaseUseCase(): IPPVPurchaseUseCase {
        return container.resolve<IPPVPurchaseUseCase>(DI.USECASES.IPPVPurchaseUseCase);
    }

    constructor() {
        // No initialization needed - dependencies resolved lazily via getters
    }

    /**
     * Add User Consent.
     * @param {string} apiKey
     * @param {IConsentRequest} paylod
     * @param {string} token
     * @param {string} identityToken
     * @returns {IConsentResponse[data]} The response for the added consent.
     */

    async addConsent(
        apiKey: string,
        paylod: IConsentRequest,
        token: string,
        identityToken: string
    ): Promise<IConsentResponse['data']> {
        return await this.addConsentUseCase.execute(apiKey, paylod, token, identityToken);
    }

    /**
     * Get the Consent data.
     * @param {string} apiKey
     * @param {string} token
     * @param {string} identityToken
     * @returns {IConsentResponse[data]} List of consent questions.
     */
    async getConsent(apiKey: string, token: string, identityToken: string): Promise<IConsentResponse['data']> {
        return await this.getConsentUseCase.execute(apiKey, token, identityToken);
    }

    /**
     * Get the PPV Event Config.
     * @returns {IPPVEventConfigResponse[data]} List of consent questions.
     */
    async getPPVEventConfig(): Promise<IPPVEventConfigResponse['data']> {
        return await this.getPPVEventConfigUseCase.execute();
    }

    /**
     * Get the PPV Event Config.
     * @returns {IPPVCountryConfigResponse[data]} List of consent questions.
     */
    async getPPVCountryConfig(country?: string): Promise<IPPVCountryConfigResponse['data']> {
        return await this.getPPVCountryConfigUseCase.execute(country);
    }

    async ppvPurchase(
        payload: IPPVPurchasePayload,
        authToken: string,
        authSign: string,
        identityToken: string
    ): Promise<IPPVPurchaseResponse> {
        const apiToken = await this.formSignatureObjectUseCase.execute();
        if (!apiToken) {
            throw new Error('Invalid API token');
        }
        const response = await this.getPPVPurchaseUseCase.execute(
            payload,
            authToken,
            authSign,
            identityToken,
            apiToken
        );
        return response;
    }
}
